import { faBars, faCaretDown, faListAlt, faListOl, faListUl, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { List as CList, ListItem as CListItem } from "../../../components/List";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Listbox } from "@headlessui/react";
import { Fragment, useCallback, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { useIntl } from "react-intl";

type Definition =
{
    type: string
    icon: IconDefinition,
    title: string
    listTag?: keyof JSX.IntrinsicElements
}

const ulTag = 'ul' as keyof JSX.IntrinsicElements;
const olTag = 'ol' as keyof JSX.IntrinsicElements;

const Definitions =
[
    { type: 'initial', icon: faListUl, title: 'Default', listTag: ulTag },
    { type: 'circle', icon: faListUl, title: 'Circle', listTag: ulTag },
    { type: 'square', icon: faListUl, title: 'Square', listTag: ulTag },
    { type: 'disc', icon: faListUl, title: 'Disc', listTag: ulTag },
    { type: 'decimal', icon: faListOl, title: 'Decimal' },
    { type: 'decimal-leading-zero', icon: faListOl, title: 'Decimal with leading zeros' },
    { type: 'upper-alpha', icon: faListOl, title: 'Upper Alpha' },
    { type: 'lower-alpha', icon: faListOl, title: 'Lower Alpha' },
    { type: 'upper-roman', icon: faListOl, title: 'Upper Roman' },
    { type: 'lower-roman', icon: faListOl, title: 'Lower Roman' },
    { type: 'upper-greek', icon: faListOl, title: 'Upper Greek' },
    { type: 'lower-greek', icon: faListOl, title: 'Lower Greek' },
    { type: 'none', icon: faBars, title: 'None' },
];

function ListOption({ definition, translations } : { definition: Definition, translations: any })
{
    const { type } = definition;
    const title = translations?.values?.[definition.type] ?? definition.title;
    const Tag = definition.listTag ?? olTag;
    const element =
    <Listbox.Option key={type} value={type} as={Fragment}>
        {
            ({ selected }) =>
            <li className={selected ? 'selected' : ''}>
                <Tag className="list-selector" style={{ listStyleType: type }}><li>{title}</li></Tag>
            </li>
        }
    </Listbox.Option>;
    return element;
}

export default function List()
{
    const [editor] = useLexicalComposerContext();
    const intl = useIntl();
    const [value, setValue] = useState(Definitions[0]);
    const onSelect = useCallback(() =>
    {
        const command = value?.listTag === ulTag ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND;
        editor.dispatchCommand(command, null as unknown as void);
    },
    [editor, value]);
    const onChange = useCallback((value: string) =>
    {
        const item = Definitions.find(item => item.type === value) ?? Definitions[0];
        const command = item?.listTag === ulTag ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND;
        setValue(item);
        editor.dispatchCommand(command, null as unknown as void);
    },
    [editor]);
    const translations = (intl as any).messages?.sharedComponents?.lexicalEditor?.lists;
    const element =
    <div className="btn-group">
        <button type="button" className="btn" onClick={onSelect}>
            <FontAwesomeIcon icon={faListUl} />
        </button>
        <Listbox value={value.type} onChange={onChange}>
            <Listbox.Button className={'btn'}>
                <FontAwesomeIcon icon={faCaretDown} className="caret" />
            </Listbox.Button>
            <Listbox.Options className='menu-content'>
                {Definitions.map((definition) => <ListOption key={definition.type} definition={definition} translations={translations} />)}
            </Listbox.Options>
        </Listbox>
    </div>;
    return element;
}
